import { useMemo } from 'react';
import { useDashboardData } from './useDashboardData';
import { TodoItem } from '../../types/todo';

export interface ProductivityMetrics {
  // Daily metrics
  todayCompletionRate: number;
  todayTasksCompleted: number;
  todayTasksTotal: number;
  
  // Weekly trends (last 7 days)
  weeklyCompletions: number[];
  weeklyCompletionRate: number;
  weeklyProductivityTrend: 'improving' | 'stable' | 'declining';
  
  // Streak metrics
  currentCompletionStreak: number;
  longestCompletionStreak: number;
  
  // Productivity scores
  productivityScore: number; // 0-100 overall score
  deadlineAdherenceScore: number; // 0-100 based on on-time completions
  consistencyScore: number; // 0-100 based on regular activity
  
  // Performance insights
  averageTaskCompletionTime: number; // in hours
  mostProductiveTimeOfDay: string; // e.g., "morning", "afternoon", "evening"
  averageTasksPerDay: number;
  
  // Comparison metrics
  thisWeekVsLastWeek: {
    completions: { current: number; previous: number; change: number };
    creations: { current: number; previous: number; change: number };
    completionRate: { current: number; previous: number; change: number };
  };
  
  // Goal tracking
  dailyGoalProgress: number; // percentage towards daily goal
  weeklyGoalProgress: number; // percentage towards weekly goal
}

/**
 * Advanced productivity metrics hook
 * Provides sophisticated analytics and insights for productivity tracking
 */
export const useProductivityMetrics = (
  dailyGoal: number = 5,
  weeklyGoal: number = 25
): ProductivityMetrics => {
  const dashboardData = useDashboardData();

  return useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Helper function to get date ranges
    const getDateRange = (days: number, fromDate = today) => {
      const endDate = new Date(fromDate.getTime() + 24 * 60 * 60 * 1000); // Next day
      const startDate = new Date(fromDate.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
      return { startDate, endDate };
    };

    // Today's metrics
    const todayStart = new Date(today);
    const todayEnd = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    
    const todaysTasks = dashboardData.todos.filter(t => {
      const createdDate = new Date(t.createdAt);
      const dueDate = t.dueDate ? new Date(t.dueDate) : null;
      return (createdDate >= todayStart && createdDate < todayEnd) ||
             (dueDate && dueDate >= todayStart && dueDate < todayEnd);
    });
    
    const todayTasksCompleted = todaysTasks.filter(t => t.isCompleted).length;
    const todayTasksTotal = todaysTasks.length;
    const todayCompletionRate = todayTasksTotal > 0 ? 
      Math.round((todayTasksCompleted / todayTasksTotal) * 100) : 0;

    // Weekly completions (last 7 days)
    const weeklyCompletions = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
      const nextDay = new Date(date.getTime() + 24 * 60 * 60 * 1000);
      
      return dashboardData.todos.filter(t => {
        if (!t.isCompleted || !t.completedAt) return false;
        const completedDate = new Date(t.completedAt);
        return completedDate >= date && completedDate < nextDay;
      }).length;
    }).reverse(); // Reverse to get chronological order

    // Weekly completion rate
    const { startDate: weekStart, endDate: weekEnd } = getDateRange(7);
    const weeklyTasks = dashboardData.todos.filter(t => {
      const createdDate = new Date(t.createdAt);
      return createdDate >= weekStart && createdDate < weekEnd;
    });
    const weeklyCompletedTasks = weeklyTasks.filter(t => t.isCompleted);
    const weeklyCompletionRate = weeklyTasks.length > 0 ? 
      Math.round((weeklyCompletedTasks.length / weeklyTasks.length) * 100) : 0;

    // Productivity trend analysis
    const recentWeekCompletions = weeklyCompletions.slice(-3); // Last 3 days
    const earlierWeekCompletions = weeklyCompletions.slice(0, 3); // First 3 days
    const recentAvg = recentWeekCompletions.reduce((a, b) => a + b, 0) / recentWeekCompletions.length;
    const earlierAvg = earlierWeekCompletions.reduce((a, b) => a + b, 0) / earlierWeekCompletions.length;
    
    let weeklyProductivityTrend: 'improving' | 'stable' | 'declining' = 'stable';
    if (recentAvg > earlierAvg * 1.1) weeklyProductivityTrend = 'improving';
    else if (recentAvg < earlierAvg * 0.9) weeklyProductivityTrend = 'declining';

    // Completion streak calculation
    const calculateCompletionStreak = (todos: TodoItem[]) => {
      let currentStreak = 0;
      let longestStreak = 0;
      let tempStreak = 0;
      
      // Sort todos by completion date
      const completedTodos = todos
        .filter(t => t.isCompleted && t.completedAt)
        .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime());
      
      // Check each day going backwards from today
      for (let i = 0; i < 30; i++) { // Check last 30 days
        const checkDate = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
        const nextDay = new Date(checkDate.getTime() + 24 * 60 * 60 * 1000);
        
        const hasCompletionOnDay = completedTodos.some(t => {
          const completedDate = new Date(t.completedAt!);
          return completedDate >= checkDate && completedDate < nextDay;
        });
        
        if (hasCompletionOnDay) {
          tempStreak++;
          if (i === 0 || currentStreak === i) { // Consecutive days
            currentStreak = tempStreak;
          }
          longestStreak = Math.max(longestStreak, tempStreak);
        } else {
          tempStreak = 0;
        }
      }
      
      return { currentStreak, longestStreak };
    };
    
    const { currentStreak: currentCompletionStreak, longestStreak: longestCompletionStreak } = 
      calculateCompletionStreak(dashboardData.todos);

    // Deadline adherence score
    const tasksWithDueDates = dashboardData.todos.filter(t => t.dueDate && t.isCompleted);
    const onTimeTasks = tasksWithDueDates.filter(t => {
      if (!t.completedAt) return false;
      return new Date(t.completedAt) <= new Date(t.dueDate!);
    });
    const deadlineAdherenceScore = tasksWithDueDates.length > 0 ? 
      Math.round((onTimeTasks.length / tasksWithDueDates.length) * 100) : 100;

    // Consistency score based on regular activity
    const activeDaysInWeek = weeklyCompletions.filter(count => count > 0).length;
    const consistencyScore = Math.round((activeDaysInWeek / 7) * 100);

    // Average task completion time
    const completedTasksWithTime = dashboardData.todos.filter(t => 
      t.isCompleted && t.completedAt && t.createdAt
    );
    const totalCompletionTime = completedTasksWithTime.reduce((total, task) => {
      const created = new Date(task.createdAt).getTime();
      const completed = new Date(task.completedAt!).getTime();
      return total + (completed - created);
    }, 0);
    const averageTaskCompletionTime = completedTasksWithTime.length > 0 ? 
      totalCompletionTime / completedTasksWithTime.length / (1000 * 60 * 60) : 0; // Convert to hours

    // Most productive time of day analysis
    const completionsByHour = Array.from({ length: 24 }, () => 0);
    dashboardData.todos
      .filter(t => t.isCompleted && t.completedAt)
      .forEach(t => {
        const hour = new Date(t.completedAt!).getHours();
        completionsByHour[hour]++;
      });
    
    const maxCompletions = Math.max(...completionsByHour);
    const mostProductiveHour = completionsByHour.indexOf(maxCompletions);
    let mostProductiveTimeOfDay = 'morning';
    if (mostProductiveHour >= 12 && mostProductiveHour < 17) mostProductiveTimeOfDay = 'afternoon';
    else if (mostProductiveHour >= 17) mostProductiveTimeOfDay = 'evening';

    // Average tasks per day
    const { startDate: monthStart } = getDateRange(30);
    const monthlyTasks = dashboardData.todos.filter(t => 
      new Date(t.createdAt) >= monthStart
    );
    const averageTasksPerDay = monthlyTasks.length / 30;

    // This week vs last week comparison
    const { startDate: thisWeekStart } = getDateRange(7);
    const { startDate: lastWeekStart, endDate: lastWeekEnd } = getDateRange(7, 
      new Date(thisWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000)
    );
    
    const thisWeekTasks = dashboardData.todos.filter(t => 
      new Date(t.createdAt) >= thisWeekStart
    );
    const lastWeekTasks = dashboardData.todos.filter(t => {
      const created = new Date(t.createdAt);
      return created >= lastWeekStart && created < lastWeekEnd;
    });
    
    const thisWeekCompletions = thisWeekTasks.filter(t => t.isCompleted).length;
    const lastWeekCompletions = lastWeekTasks.filter(t => t.isCompleted).length;
    const thisWeekCreations = thisWeekTasks.length;
    const lastWeekCreations = lastWeekTasks.length;
    
    const thisWeekCompletionRate = thisWeekCreations > 0 ? 
      (thisWeekCompletions / thisWeekCreations) * 100 : 0;
    const lastWeekCompletionRate = lastWeekCreations > 0 ? 
      (lastWeekCompletions / lastWeekCreations) * 100 : 0;
    
    const thisWeekVsLastWeek = {
      completions: {
        current: thisWeekCompletions,
        previous: lastWeekCompletions,
        change: thisWeekCompletions - lastWeekCompletions
      },
      creations: {
        current: thisWeekCreations,
        previous: lastWeekCreations,
        change: thisWeekCreations - lastWeekCreations
      },
      completionRate: {
        current: Math.round(thisWeekCompletionRate),
        previous: Math.round(lastWeekCompletionRate),
        change: Math.round(thisWeekCompletionRate - lastWeekCompletionRate)
      }
    };

    // Goal progress
    const dailyGoalProgress = Math.min(100, Math.round((todayTasksCompleted / dailyGoal) * 100));
    const weeklyGoalProgress = Math.min(100, Math.round((thisWeekCompletions / weeklyGoal) * 100));

    return {
      // Daily metrics
      todayCompletionRate,
      todayTasksCompleted,
      todayTasksTotal,
      
      // Weekly trends
      weeklyCompletions,
      weeklyCompletionRate,
      weeklyProductivityTrend,
      
      // Streak metrics
      currentCompletionStreak,
      longestCompletionStreak,
      
      // Productivity scores
      productivityScore: dashboardData.productivityScore,
      deadlineAdherenceScore,
      consistencyScore,
      
      // Performance insights
      averageTaskCompletionTime: Math.round(averageTaskCompletionTime * 10) / 10, // Round to 1 decimal
      mostProductiveTimeOfDay,
      averageTasksPerDay: Math.round(averageTasksPerDay * 10) / 10,
      
      // Comparison metrics
      thisWeekVsLastWeek,
      
      // Goal tracking
      dailyGoalProgress,
      weeklyGoalProgress
    };
  }, [dashboardData, dailyGoal, weeklyGoal]);
};